<template>
    <SlideOver size="xxl" :show="!!station" @close="closeStation()" :title="stationTitle">
        <div v-if="station">
            <div v-if="station.description" class="py-2">
                <p class="text-sm text-gray-700 whitespace-pre-line" v-html="station.description" />
            </div>
            <div v-if="station.activityResponse" class="py-2">
                <BadgePill :color="station.activityResponse.color">{{ station.activityResponse.letter }}</BadgePill>
            </div>
            <div v-if="station.type === 'url'">
                <a :href="station.url" class="text-indigo-500" target="_blank" @click="complete">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        class="h-6 w-6 inline-block"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                        ></path>
                    </svg>
                    {{ $tc('messages.links', 1) }}
                </a>
            </div>
            <div v-else-if="station.type === 'quiz'">
                <AppButton desired-action :href="station.activity_url">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                        <path
                            fill-rule="evenodd"
                            d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $tc('messages.quizzes', 1) }}
                </AppButton>
            </div>
            <div v-else-if="station.type === 'rubric'">
                <AppButton desired-action :href="station.activity_url">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                        <path
                            fill-rule="evenodd"
                            d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $tc('messages.rubrics', 1) }}
                </AppButton>
            </div>
            <div v-else-if="station.type === 'gamification_chest'">
                <div class="md:w-1/3">
                    <TreasureChest
                        :type="station.settings.type"
                        class="object-contain"
                        :is-open="!!station.completed_at"
                        :class="{ 'saturate-0': station.is_locked, 'cursor-pointer': !station.completed_at }"
                        @click="
                            completeTreasureChest();
                            station.completed_at = true;
                        "
                    />
                    <div class="mt-1">
                        <div class="flex justify-between items-center">
                            <div>
                                <BadgePill color="green" v-show="point.points">
                                    {{ point.points }}
                                </BadgePill>
                            </div>
                            <div>
                                <BadgePill color="yellow" v-show="point.coins_points">
                                    {{ point.coins_points }}
                                </BadgePill>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-5" v-if="point.items">
                    <TransitionGroup
                        enter-active-class="transition-opacity ease-linear duration-300"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-opacity ease-linear duration-300"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                        tag="div"
                        class="grid grid-cols-3 lg:grid-cols-6 md:gap-6"
                    >
                        <div v-for="(item, index) in point.items" :key="index">
                            <div class="relative h-40">
                                <img :src="'/img/gamification/shine.svg'" alt="" class="absolute animate-spin-slow" />
                                <img :src="item.image_path" alt="" class="absolute object-contain" />
                            </div>
                        </div>
                    </TransitionGroup>
                </div>
                <div v-auto-animate>
                    <div class="mt-3 flex gap-2" v-if="station.completed_at">
                        <AppButton element="button" @click="closeStation" desired-action>
                            {{ $t('messages.return_to_itinerary') }}
                        </AppButton>
                        <AppButton :href="route('students.inventory.index')">
                            {{ $t('messages.inventory') }}
                        </AppButton>
                    </div>
                </div>
            </div>
        </div>
    </SlideOver>
</template>

<script>
export default {
    name: 'StudentStationPanel',
};
</script>
<script setup>
import axios from 'axios';

let emit = defineEmits(['close']);
import SlideOver from '@/components/SlideOver.vue';
import { computed, ref, watch } from 'vue';
import AppButton from '@/components/AppButton.vue';
import TreasureChest from '@/components/TreasureChest.vue';
import BadgePill from '@/components/BadgePill.vue';
import { Howl } from 'howler';

const props = defineProps({
    station: {
        type: Object,
    },
});

const stationTitle = computed(() => {
    return props.station ? props.station.display_name : null;
});

const point = ref({
    points: null,
    coins_points: null,
    items: [],
});
const isMarkedForUpdate = ref(false);

const markAsForUpdate = () => {
    isMarkedForUpdate.value = true;
};
const complete = () => {
    axios.patch(props.station.links.complete);
    markAsForUpdate();
};

if (!props.station.completed_at && props.station.type !== 'gamification_chest') {
    setTimeout(() => {
        markAsForUpdate();
    }, 1000);
}

function completeTreasureChest() {
    if (!props.station.completed_at) {
        axios.patch(props.station.links.complete).then((response) => {
            point.value.points = response.data.point.points;
            point.value.coins_points = response.data.point.coins_points;
            response.data.point.items.forEach((item, i) => {
                setTimeout(() => {
                    point.value.items.push(item);
                }, 500 * i);
            });
            setTimeout(() => {
                const sound = new Howl({
                    src: ['/assets/gamification/open_chest.mp3'],
                });
                sound.play();
            }, 1000);
        });
        markAsForUpdate();
    }
}

const initialize = () => {
    axios.patch(props.station.links.initialize);
};

watch(
    () => props.station,
    (currentStation) => {
        if (currentStation) {
            initialize();
        }
    },
    { immediate: true },
);

const closeStation = () => {
    emit('close', isMarkedForUpdate.value);
};
</script>
